import React from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import prop from 'lodash/fp/prop'
import { Breadcrumbs, Crumb } from '@carsys/ui'
import {
  WorkorderLineState,
  getLineGenericArticleDescription,
  getProductTitle,
  useGetCurrentSelectedOrderAccount
} from 'app/workorder/state/workorder/selectors'
import {
  OrderDetailUrl,
  OrderOpenApprovalsUrl,
  OrderPromotionItemUrl,
  OrderPromotionsListUrl,
  OrderQuotationDetailUrl,
  OrderQuotationsUrl,
  OrderUrl
} from 'app/order/RouteConstants'
import { useProductGroupCrumbs } from './ProductGroupCrumbs'
import PromotionItemBreadCrumb from 'app/wholesaler/cms/components/promotions/PromotionItemBreadCrumb'

export const PortalOrdersBreadCrumbs: React.FC<React.ComponentProps<typeof Breadcrumbs>> = ({ ...props }) => {
  const { t } = useTranslation()
  const lines = useSelector(state => state.order.lines)
  const product = useSelector(state => state.products.selectedProduct)
  const productGroups = useSelector(state => state.products.categories)
  const workorderID = useSelector(state => state.order.details?.Workorder?.ID)
  const selectedOrderAccount = useGetCurrentSelectedOrderAccount()

  const productGroupCrumbs = useProductGroupCrumbs({ productGroups, path: '/orders/:uuid/product-groups/:productGroupUUID' })

  return (
    <Breadcrumbs {...props}>
      <Crumb data-testid='crumb-orders' key='crumb-orders' path={OrderUrl} text={t('order.orders')} />,
      <Crumb
        data-testid='crumb-orders-uuid-view'
        key='crumb-orders-uuid-view'
        path={OrderDetailUrl}
        text={`${t('order.order')} (${workorderID})`}
      />
      <Crumb
        data-testid='crumb-orders-open-approvals-list'
        key='crumb-orders-open-approvals-list'
        path={OrderOpenApprovalsUrl}
        text={t('order.ApprovalsTitle')}
      />
      <Crumb
        data-testid='crumb-orders-quotations-list'
        key='crumb-orders-quotations-list'
        path={OrderQuotationsUrl}
        text={t('VehicleSales.QuotationsListTitle')}
      />
      <Crumb
        data-testid='crumb-orders-quotations-detail'
        key='crumb-orders-quotations-detail'
        path={OrderQuotationDetailUrl}
        text={t('VehicleSales.QuotationTitle')}
      />
      <Crumb
        data-testid='crumb-orders-view-product-groups'
        key='crumb-orders-view-product-groups'
        path='/orders/:uuid/product-groups'
        text={t('product.product_groups')}
      />
      <Crumb
        data-testid='crumb-orders-uuid-searchterm'
        key='crumb-orders-uuid-searchterm'
        path='/orders/:uuid/search-products/:orderAccountUUID/:searchTerm'
        text={params => `${t('global.search_breadcrumb')} (${decodeURIComponent(params.searchTerm)})`}
      />
      <Crumb
        data-testid='crumb-orders-view-order'
        key='crumb-orders-view-order'
        path='/orders/:uuid/order'
        text={t('workorder.order_parts')}
      />
      <Crumb
        data-testid='crumb-orders-view-order-step-2'
        key='crumb-orders-view-order-step-2'
        path='/orders/:uuid/order/step2'
        text={prop('selectedOrderAccount.Wholesaler.Name', selectedOrderAccount)}
      />
      <Crumb
        data-testid='crumb-orders-historic-parts'
        key='crumb-orders-historic-parts'
        path='/orders/:uuid/historic-parts'
        text={t('vehicle.order_history')}
      />
      <Crumb
        data-testid='crumb-orders-haynespro-repair-times'
        key='crumb-orders-haynespro-repair-times'
        path='/orders/:uuid/*haynes/repair-times'
        text={t('vehicle.haynes_repair_times')}
      />
      <Crumb
        data-testid='crumb-orders-haynespro-iframe'
        key='crumb-orders-haynespro-iframe'
        path='/orders/:uuid/*haynes/frame'
        text={t('workorder.haynes_iframe')}
      />
      <Crumb
        data-testid='crumb-orders-haynespro-service-schedule'
        key='crumb-orders-haynespro-service-schedule'
        path='/orders/:uuid/*haynes/service-schedule/:conditionUUID?'
        text={t('vehicle.haynes_service_schedule')}
      />
      <Crumb
        data-testid='crumb-orders-haynespro-service-schedule-intervaldata'
        key='crumb-orders-haynespro-service-schedule-intervaldata'
        path='/orders/:uuid/*haynes/service-schedule/:conditionUUID?/intervaldata'
        text={t('vehicle.haynes_service_interval_data')}
      />
      <Crumb
        data-testid='crumb-workorders-view-link-account'
        key='crumb-workorders-view-link-account'
        path='/orders/:uuid/link-account'
        text={t('workorder.link_account')}
      />
      <Crumb
        data-testid='crumb-orders-line-view-products'
        key='crumb-orders-line-view-products'
        path='/orders/:workorderUUID/*line/:lineUUID/products'
        text={params => getLineGenericArticleDescription(lines as WorkorderLineState[], params.lineUUID)}
      />
      <Crumb data-testid='crumb-orders-view-print' key='crumb-orders-view-print' path='/orders/:uuid/print' text={t('global.print')} />
      <Crumb
        data-testid='crumb-orders-view-product-groups-view-products-view'
        key='crumb-orders-view-product-groups-view-products-view'
        path='/orders/:uuid/*/products/:productID'
        text={() => getProductTitle(product)}
      />
      <Crumb
        data-testid='crumb-orders-checklist-view-print'
        key='crumb-orders-checklist-view-print'
        path='/orders/:uuid/checklist/:checklistUUID/print'
        text={t('workorder.checklist_print')}
      />
      <Crumb
        data-testid='crumb-catalogs-universalparts'
        key='crumb-catalogs-universalparts'
        path='/orders/*/catalogs/universal-parts'
        text={t('catalogs.UniversalParts')}
      />
      <Crumb
        data-testid='crumb-catalogs-toolspecial'
        key='crumb-catalogs-toolspecial'
        path='/orders/*/catalogs/tool-special'
        text={t('catalogs.ToolSpecial')}
      />
      <Crumb
        data-testid='crumb-wholesaler-cms-promotions-list-in-order'
        key='crumb-wholesaler-cms-promotions-list-in-order'
        path={OrderPromotionsListUrl}
        text={t('cms-promotion.promotion-title')}
      />
      <Crumb
        data-testid='crumb-wholesaler-cms-promotions-item-in-order'
        key='crumb-wholesaler-cms-promotions-item-in-order'
        path={OrderPromotionItemUrl}
      >
        <PromotionItemBreadCrumb />
      </Crumb>
      {!!productGroupCrumbs && React.Children.toArray(productGroupCrumbs)}
    </Breadcrumbs>
  )
}

export default PortalOrdersBreadCrumbs
